import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, X, Edit2 } from 'lucide-react';
import { generateId } from '@/shared/lib/id';
import { WallElement } from '@/shared/model';

interface WallElementsManagerProps {
  elements: WallElement[];
  onChange: (elements: WallElement[]) => void;
  roomHeight: number;
}

export const WallElementsManager: FC<WallElementsManagerProps> = ({
  elements,
  onChange,
  roomHeight
}) => {
  const { t } = useTranslation();
  
  const [isAddingWallElement, setIsAddingWallElement] = useState(false);
  const [newWallElement, setNewWallElement] = useState({
      type: 'niche' as 'niche' | 'ledge',
      width: '' as string | number,
      height: '' as string | number,
      depth: '' as string | number,
      isFullHeight: false,
      includeInTotal: true
  });
  const [editingWallElementId, setEditingWallElementId] = useState<string | null>(null);

  // Открытие формы добавления: высота автоматически подставляется из высоты помещения
  // (в см), но остаётся редактируемой под кастомное значение (запрошено пользователем).
  const startAddWallElement = () => {
    setEditingWallElementId(null);
    setNewWallElement({
        type: 'niche',
        width: '',
        height: roomHeight ? Math.round(roomHeight * 100) : '',
        depth: '',
        isFullHeight: false,
        includeInTotal: true,
    });
    setIsAddingWallElement(true);
  };

  const handleSaveWallElement = () => {
    const wStr = String(newWallElement.width !== undefined && newWallElement.width !== null ? newWallElement.width : '').trim();
    if (!wStr || !onChange) return;

    const widthM = parseFloat(wStr) / 100;
    if (isNaN(widthM) || widthM <= 0) return;

    const elementData: WallElement = {
        id: editingWallElementId || generateId(),
        type: newWallElement.type,
        width: widthM,
        height: newWallElement.height ? parseFloat(String(newWallElement.height)) / 100 : 0,
        depth: parseFloat(String(newWallElement.depth)) / 100 || 0,
        isFullHeight: newWallElement.isFullHeight,
        includeInTotal: newWallElement.includeInTotal
    };

    if (editingWallElementId) {
        onChange(elements.map(el => el.id === editingWallElementId ? elementData : el));
        setEditingWallElementId(null);
    } else {
        onChange([...elements, elementData]);
    }

    setNewWallElement({ type: 'niche', width: '', height: '', depth: '', isFullHeight: false, includeInTotal: true });
    setIsAddingWallElement(false);
  };

  const handleEditWallElement = (el: WallElement) => {
    setNewWallElement({
        type: el.type,
        width: Math.round(Number(el.width) * 100),
        height: el.height ? Math.round(Number(el.height) * 100) : '',
        depth: Math.round(Number(el.depth) * 100),
        isFullHeight: !!el.isFullHeight,
        includeInTotal: el.includeInTotal !== false
    });
    setEditingWallElementId(el.id || null);
    setIsAddingWallElement(true);
  };

  return (
    <div className="mb-6 border-t border-slate-100 dark:border-slate-800 pt-4">
        <div className="flex justify-between items-center mb-3">
            <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('room_input.wall_elements_title')}</div>
            {!isAddingWallElement && (
                <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={startAddWallElement}
                    className="text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1 rounded transition-colors"
                >
                    <Plus size={14}/> {t('common.add')}
                </motion.button>
            )}
        </div>

        <AnimatePresence>
            {isAddingWallElement && (
                <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-700 mb-3 overflow-hidden"
                >
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{editingWallElementId ? t('room_input.edit_element') : t('room_input.new_element')}</span>
                        <button 
                            onClick={() => { setIsAddingWallElement(false); setEditingWallElementId(null); }} 
                            className="p-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer"
                            aria-label={t('common.close')}
                        >
                            <X size={16} aria-hidden="true"/>
                        </button>
                    </div>
                    
                    <div className="flex bg-slate-100 dark:bg-slate-700 p-1 rounded-lg mb-3">
                        <button 
                            type="button"
                            onClick={() => setNewWallElement({...newWallElement, type: 'niche'})}
                            className={`flex-1 py-1.5 rounded-md text-[10px] font-bold transition-all cursor-pointer ${newWallElement.type === 'niche' ? 'bg-white dark:bg-slate-600 text-blue-600 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                        >
                            {t('room_input.niche')}
                        </button>
                        <button 
                            type="button"
                            onClick={() => setNewWallElement({...newWallElement, type: 'ledge'})}
                            className={`flex-1 py-1.5 rounded-md text-[10px] font-bold transition-all cursor-pointer ${newWallElement.type === 'ledge' ? 'bg-white dark:bg-slate-600 text-amber-600 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                        >
                            {t('room_input.ledge')}
                        </button>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-3">
                        <input 
                            type="number" placeholder={t('room_input.width_cm_placeholder')}
                            className="p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                            value={newWallElement.width}
                            onChange={e => setNewWallElement({...newWallElement, width: e.target.value})}
                        />
                        <input 
                            type="number" placeholder={t('room_input.height_cm_short_placeholder')}
                            className="p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                            value={newWallElement.height}
                            onChange={e => setNewWallElement({...newWallElement, height: e.target.value})}
                        />
                        <input 
                            type="number" placeholder={t('room_input.depth_cm_placeholder')}
                            className="p-2 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500 dark:text-slate-100"
                            value={newWallElement.depth}
                            onChange={e => setNewWallElement({...newWallElement, depth: e.target.value})}
                        />
                    </div>

                    <div className="space-y-2 mb-4">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input 
                                type="checkbox" 
                                checked={newWallElement.isFullHeight}
                                onChange={e => setNewWallElement({...newWallElement, isFullHeight: e.target.checked})}
                                className="w-4 h-4 rounded text-blue-600"
                            />
                            <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400">{t('room_input.full_height_label')}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input 
                                type="checkbox" 
                                checked={newWallElement.includeInTotal}
                                onChange={e => setNewWallElement({...newWallElement, includeInTotal: e.target.checked})}
                                className="w-4 h-4 rounded text-blue-600"
                            />
                            <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400">{t('room_input.include_total_label')}</span>
                        </label>
                    </div>

                    <button 
                        onClick={handleSaveWallElement}
                        className="w-full py-2 bg-blue-600 text-white rounded font-bold text-sm"
                    >
                        {editingWallElementId ? t('common.save') : t('common.add')}
                    </button>
                </motion.div>
            )}
        </AnimatePresence>

        <div className="space-y-2">
            {elements.map(el => (
                <motion.div 
                    key={el.id}
                    layout
                    className="flex justify-between items-center p-2 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded text-sm shadow-sm group"
                >
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded flex items-center justify-center ${el.type === 'niche' ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                            {el.type === 'niche' ? '凹' : '凸'}
                        </div>
                        <div>
                            <div className="font-bold text-slate-700 dark:text-slate-200 text-xs uppercase flex items-center gap-2">
                                {el.type === 'niche' ? t('room_input.niche') : t('room_input.ledge')}
                                {el.includeInTotal && <span className="text-[9px] bg-blue-100 text-blue-600 px-1 rounded">AUTO</span>}
                            </div>
                            {/* eslint-disable i18next/no-literal-string */}
                            <div className="text-slate-500 dark:text-slate-400 text-[10px]">
                                <span className="font-mono tabular-nums">{Math.round(Number(el.width) * 100)}</span>x<span className="font-mono tabular-nums">{el.height ? Math.round(Number(el.height) * 100) : 'H'}</span>x<span className="font-mono tabular-nums">{Math.round(Number(el.depth) * 100)}</span>{t('room_input.cm_suffix', 'см')}
                            </div>
                            {/* eslint-enable i18next/no-literal-string */}
                        </div>
                    </div>
                    <div className="flex gap-1">
                        <button 
                            onClick={() => handleEditWallElement(el)} 
                            className="p-2 text-slate-400 hover:text-blue-500 transition-colors cursor-pointer"
                            aria-label={t('common.edit')}
                        >
                            <Edit2 size={16} aria-hidden="true"/>
                        </button>
                        <button 
                            onClick={() => onChange(elements.filter(x => x.id !== el.id))} 
                            className="p-2 text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                            aria-label={t('common.delete')}
                        >
                            <Trash2 size={16} aria-hidden="true"/>
                        </button>
                    </div>
                </motion.div>
            ))}
        </div>
    </div>
  );
};